/**
 * A one-way "please open the enroll-queue dock" channel.
 *
 * WHY A MODULE SINGLETON rather than the queue context. The requester is the
 * view-system action dispatcher, which renders on surfaces that are NOT inside
 * `EnrollQueueProvider` (the focused-artifact page, the admin action previews) —
 * calling `useEnrollQueue()` there would throw. And the dispatcher has no business
 * knowing what a queue is: it is told what to open by the action binding's
 * `interactionConfig.onSuccess`, which is data.
 *
 * Same shape as `impersonate-write-mode.ts`: a module-scoped value with explicit
 * subscribers, no React context, no event-name strings.
 *
 * A REQUEST WITH NO SUBSCRIBER IS HELD, NOT DROPPED.
 *
 * The dock is mounted app-wide, so "nobody is listening" almost always means
 * "listening a tick from now" — the component is remounting, recovering from an
 * error boundary, or the request beat the effect that subscribes. Dropping it there
 * produced the failure this file exists to fix: the action succeeded, the queue was
 * loaded, and the UI sat still, which reads as the feature being broken.
 *
 * So one pending request is remembered and delivered to the next subscriber. One,
 * not a queue: two approvals before the dock mounts still mean "open the dock",
 * and replaying twice would just fight itself. It is cleared on delivery, so a
 * later mount (a fresh navigation, hours on) never re-opens the dock unasked.
 */
type Listener = () => void;

const listeners = new Set<Listener>();
let pending = false;

export const enrollQueueOpenRequest = {
  /**
   * Subscribe the mounted dock. Returns the unsubscribe for effect cleanup.
   *
   * Delivers a held request immediately — that is the whole point of holding one.
   */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    if (pending) {
      pending = false;
      try {
        listener();
      } catch {
        // Same reasoning as below: a throwing subscriber must not break the
        // subscription itself, or the dock would be permanently deaf.
      }
    }
    return () => { listeners.delete(listener); };
  },

  /** Ask the dock to open. Held for the next subscriber when nothing is listening. */
  request(): void {
    if (listeners.size === 0) {
      pending = true;
      return;
    }
    // A copy, so a listener that unsubscribes during dispatch cannot mutate the
    // set mid-iteration.
    for (const listener of [...listeners]) {
      try {
        listener();
      } catch {
        // One broken subscriber must not stop the others, and must never surface
        // as a failed action — the action already succeeded by this point.
      }
    }
  },

  /** Test-only: forget any held request so cases cannot leak into each other. */
  __resetForTests(): void {
    pending = false;
  },
};
